/*
 * Dark-mode blast-radius note (shared component — affects ALL consumers):
 * This component adds `border border-border` to the dialog container className.
 * In dark mode, globals.css `.dark [role="dialog"].rounded-lg` only sets
 * background-color to surface-alt (#1A1A1A) — intentionally NO border there,
 * to avoid double-border with this component's `border border-border` class.
 * Consumers: every modal in the application that uses the ui/Modal variant.
 */
import React, { useEffect, useRef, useId } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export enum ModalSize {
  Sm = "sm",
  Md = "md",
  Lg = "lg",
  Xl = "xl",
  Xxl = "2xl",
  Xxxl = "3xl",
  Fullscreen = "fullscreen",
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: ModalSize;
  "data-testid"?: string; // Made mandatory for automation testing
  preventCloseOnBackdrop?: boolean;
  preventCloseOnEsc?: boolean;
  triggerRef?: React.RefObject<HTMLElement>;
  overlayClassName?: string | undefined;
  wrapperClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  /** Optional: override the aria-labelledby id (e.g. when the dialog title is rendered inside children rather than in the Modal header) */
  ariaLabelledBy?: string;
}

const sizeClasses = {
  [ModalSize.Sm]: "max-w-md",
  [ModalSize.Md]: "max-w-lg",
  [ModalSize.Lg]: "max-w-2xl",
  [ModalSize.Xl]: "max-w-4xl",
  [ModalSize.Xxl]: "max-w-6xl",
  [ModalSize.Xxxl]: "max-w-7xl",
  [ModalSize.Fullscreen]: "w-full",
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = ModalSize.Lg,
  "data-testid": testId = "modal",
  preventCloseOnBackdrop = false,
  preventCloseOnEsc = false,
  triggerRef,
  overlayClassName,
  wrapperClassName,
  contentClassName,
  bodyClassName,
  ariaLabelledBy,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Focus trap and lifecycle management
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "unset";
      triggerElementRef.current?.focus({ preventScroll: true });
      triggerElementRef.current = null;
      return undefined;
    }

    // Store the element that was focused before the modal opened
    triggerElementRef.current = triggerRef?.current || (document.activeElement as HTMLElement);

    document.body.style.overflow = "hidden";

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return undefined;

    const firstElement = focusableElements[0];

    // Set initial focus — prefer the first form field (input/select/textarea or
    // CustomSelect Listbox.Button), fall back to the close button, then firstElement.
    const closeButton = modalRef.current?.querySelector<HTMLElement>('button[data-testid$="-close-button"]');
    const timer = setTimeout(() => {
      const firstField = modalRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]), ' +
        'button[aria-haspopup="listbox"]:not([disabled]), ' +
        '[role="button"][aria-haspopup="listbox"]:not([disabled])'
      );
      const focusTarget = firstField ?? closeButton ?? firstElement;
      focusTarget.focus();
      if (firstField instanceof HTMLInputElement || firstField instanceof HTMLTextAreaElement) {
        firstField.select();
      }
    }, 300);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      if (!modalRef.current) return;

      // Only act when this modal is the active dialog — i.e. focus is already
      // inside it, or focus has escaped the page and needs to be pulled in.
      // When focus is inside a *different* dialog (e.g. a nested alertdialog),
      // leave that dialog's own Tab trap in charge.
      const activeEl = document.activeElement;
      const isInsideThisModal = modalRef.current.contains(activeEl);
      const isInsideOtherDialog =
        Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"],[role="alertdialog"]'))
          // Exclude the current modal and any CSS-hidden dialogs (display:none / ancestor display:none).
          // offsetParent is null for elements that are not rendered — this prevents a hidden-but-mounted
          // dialog from causing the trap to incorrectly yield when that dialog contains the active element.
          .filter(d => d !== modalRef.current && d.offsetParent !== null)
          .some(d => d.contains(activeEl));

      // If focus is inside another dialog, don't interfere.
      if (isInsideOtherDialog) return;

      // If focus is also inside a portal dropdown that belongs to this modal, allow free Tab.
      const portalContainers = document.querySelectorAll('[data-portal-dropdown="true"]');
      for (const portal of Array.from(portalContainers)) {
        if (portal.contains(activeEl)) return;
      }

      // If focus is not inside this modal at all and not inside another dialog,
      // pull it in by wrapping (handles the case where Google Maps captures focus
      // into an iframe that is outside modalRef's DOM subtree).
      if (!isInsideThisModal) {
        // Re-query focusable elements
        const currentFocusableElements = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
        });
        if (currentFocusableElements.length === 0) return;
        event.preventDefault();
        if (event.shiftKey) {
          currentFocusableElements[currentFocusableElements.length - 1].focus();
        } else {
          currentFocusableElements[0].focus();
        }
        return;
      }

      // Re-query focusable elements on every Tab key press to account for dynamic content
      // Use the generic CSS selector for focusable elements, taking dynamic disabling into account
      const currentFocusableElements = Array.from(
        modalRef.current?.querySelectorAll<HTMLElement>(
          'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter(el => {
        // Ignore elements that are visually hidden or disabled
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
      });
      
      if (currentFocusableElements.length === 0) return;
      
      const currentFirst = currentFocusableElements[0];
      const currentLast = currentFocusableElements[currentFocusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === currentFirst) {
          event.preventDefault();
          currentLast.focus();
        }
      } else {
        // Tab
        if (document.activeElement === currentLast) {
          event.preventDefault();
          currentFirst.focus();
        }
      }
    };

    // Attach at document level (not modalRef) so the trap fires even when
    // Google Maps or other third-party widgets capture keyboard events inside
    // iframes or shadow-DOM nodes that are outside modalRef's DOM subtree.
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
      // Restore focus on cleanup (covers both isOpen→false toggle and component unmount).
      // WCAG 2.4.3 Focus Order: if the modal unmounts while isOpen=true (e.g. route change),
      // the !isOpen branch above never runs, so focus would be lost to document.body.
      // Calling focus() here ensures focus always returns to the trigger element.
      if (triggerElementRef.current) {
        const trigger = triggerElementRef.current;
        triggerElementRef.current = null;
        requestAnimationFrame(() => { trigger.focus({ preventScroll: true }); });
      }
    };
  }, [isOpen, triggerRef]);

  // Handle ESC key press:
  //  - If an editable field (input / select / textarea) is currently focused,
  //    blur it and move focus to the modal's close button (first Esc press).
  //  - Otherwise close the modal (second Esc press, or Esc from non-editable element).
  // This follows the ARIA Authoring Practices Guide pattern for modal dialogs.
  //
  // IMPORTANT: registered in CAPTURE phase (useCapture=true) so this handler fires
  // before React's synthetic event delegation (React 17+ delegates to the root DOM
  // node, not document). Child components that call e.stopPropagation() on the
  // React synthetic event would otherwise block this document-level handler from
  // ever running — capture phase bypasses that completely.
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !isOpen || preventCloseOnEsc) return;

      const active = document.activeElement as HTMLElement | null;
      const isEditableField =
        active instanceof HTMLInputElement ||
        active instanceof HTMLSelectElement ||
        active instanceof HTMLTextAreaElement;

      // If a CalendarPicker portal is currently open (rendered via createPortal into
      // document.body by DatePickerWithCalendar), let its own Escape handler close it
      // first — don't intercept the keypress here.
      // The portal div has role="dialog" AND data-testid ending in "-calendar" set by
      // DatePickerWithCalendar, so this selector reliably detects an open calendar.
      const calendarPortalOpen = !!document.querySelector('[role="dialog"][data-testid$="-calendar"]');

      if (calendarPortalOpen) {
        // Calendar is open — let DatePickerWithCalendar's own capture-phase ESC
        // handler (registered with useCapture=true) close it. Do not interfere.
        return;
      }

      if (isEditableField && modalRef.current?.contains(active)) {
        // First Esc: blur the field and move focus to the close button (or modal root).
        // stopImmediatePropagation so no other capture/bubble listeners (e.g. child
        // dropdown handlers) also react to this same Escape keydown.
        event.preventDefault();
        event.stopImmediatePropagation();
        active.blur();
        const closeBtn = modalRef.current?.querySelector<HTMLElement>(
          'button[data-testid$="-close-button"]'
        );
        if (closeBtn) {
          closeBtn.focus();
        } else {
          // No close button (e.g. no title prop) — focus modal container.
          modalRef.current?.focus();
        }
      } else {
        // Second Esc (or Esc from non-editable element): close the modal.
        onClose();
      }
    };

    if (isOpen) {
      // Capture phase: fires before React synthetic events and before child
      // bubble-phase handlers, guaranteeing consistent ESC behaviour.
      document.addEventListener("keydown", handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [isOpen, onClose, preventCloseOnEsc]);

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 overflow-y-auto ${overlayClassName || 'z-[250]'}`} data-testid={testId}>
      {/* Backdrop — aria-hidden so screen readers skip it; tabIndex={-1} keeps it out of tab order */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        aria-hidden="true"
        role="presentation"
        className="fixed inset-0 bg-black/50 transition-opacity cursor-default"
        onClick={!preventCloseOnBackdrop ? (e) => { e.stopPropagation(); onClose(); } : undefined}
        data-testid={`${testId}-backdrop`}
      />

      {/* Modal Content — onClick stopPropagation on this wrapper div prevents clicks inside
          the modal from bubbling to parent overlay/sidebar close handlers. This plain div
          (no ARIA role) satisfies jsx-a11y since it is not an interactive element. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={`flex min-h-full items-center justify-center p-4 ${wrapperClassName || ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy ?? (title ? titleId : undefined)}
          tabIndex={-1}
          className={`relative w-full ${sizeClasses[size]} bg-surface rounded-lg shadow-xl outline-none border border-border ${contentClassName || ""}`}
          data-testid={`${testId}-content`}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 id={titleId} className="text-xl font-medium text-text">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-text hover:text-text transition-colors"
                data-testid={`${testId}-close-button`}
                aria-label={t('common.close', 'Close')}
                title={t('common.close', 'Close')}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Body */}
          <div className={`p-6 ${bodyClassName || ""}`}>{children}</div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
